/* <vsr-compare> — before / after image comparison.
   See docs/js-notes/vsr-compare.md#note-1. */

(function () {
  'use strict';

  const BEFORE_TOKEN = '__BEFORE__';
  const AFTER_TOKEN = '__AFTER__';

  class VsrCompare extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-compare-range]');
      if (!this.input) return;

      this.pointerId = null;
      this.valueTextTemplate = this.input.getAttribute('data-valuetext-template') || '';

      this.boundInput = this.onInput.bind(this);
      this.boundPointerDown = this.onPointerDown.bind(this);
      this.boundPointerUp = this.onPointerUp.bind(this);
      this.boundPointerCancel = this.onPointerCancel.bind(this);
      this.boundKeyDown = this.onKeyDown.bind(this);
      this.boundBlur = this.onBlur.bind(this);

      const move = this.onPointerMove.bind(this);
      this.boundPointerMove = window.VSR?.rafThrottle ? window.VSR.rafThrottle(move) : move;

      this.input.addEventListener('input', this.boundInput);
      this.input.addEventListener('keydown', this.boundKeyDown);
      this.input.addEventListener('blur', this.boundBlur);
      this.addEventListener('pointerdown', this.boundPointerDown);
      this.addEventListener('pointermove', this.boundPointerMove);
      this.addEventListener('pointerup', this.boundPointerUp);
      this.addEventListener('pointercancel', this.boundPointerCancel);

      // Only now does the frame own pointer input. Until this attribute lands the
      // native range is the interactive surface, so the markup degrades cleanly.
      this.dataset.ready = 'true';
      this.render();
    }

    disconnectedCallback() {
      if (!this.input) return;

      this.releasePointer();
      this.input.removeEventListener('input', this.boundInput);
      this.input.removeEventListener('keydown', this.boundKeyDown);
      this.input.removeEventListener('blur', this.boundBlur);
      this.removeEventListener('pointerdown', this.boundPointerDown);
      this.removeEventListener('pointermove', this.boundPointerMove);
      this.removeEventListener('pointerup', this.boundPointerUp);
      this.removeEventListener('pointercancel', this.boundPointerCancel);

      delete this.dataset.ready;
      delete this.dataset.dragging;
      delete this.dataset.pointerFocus;
      this.input = null;
    }

    /* ---- Pointer ------------------------------------------------------------ */

    onPointerDown(event) {
      if (!event.isPrimary) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      // Stops image dragging and text selection without touching scrolling,
      // which `touch-action: pan-y` on the frame already handles.
      event.preventDefault();

      this.pointerId = event.pointerId;
      this.moved = false;
      this.setPointerCapture(event.pointerId);
      this.dataset.dragging = 'true';

      // A drag is an interaction with the slider, so hand it the focus a native
      // control would have taken — press an arrow key straight after a drag and it
      // keeps going. Chrome treats programmatic focus on a range as keyboard
      // focus, though, so it would paint a focus ring after an ordinary click.
      // This flag tells the stylesheet the focus came from a pointer; the first
      // keystroke or a blur clears it and the ring behaves as it should.
      this.dataset.pointerFocus = 'true';
      this.input.focus({ preventScroll: true });

      // A mouse or pen press is unambiguous, so it jumps the divider at once. A
      // finger is not: the same touch may turn out to be a page scroll, and the
      // divider twitching as the page moves away would be sloppy. Touch therefore
      // waits until it actually travels — `touch-action: pan-y` guarantees a
      // vertical gesture arrives as pointercancel rather than pointermove.
      if (event.pointerType !== 'touch') this.applyFrom(event);
    }

    onKeyDown() {
      delete this.dataset.pointerFocus;
    }

    onBlur() {
      delete this.dataset.pointerFocus;
    }

    onPointerMove(event) {
      if (!this.isActivePointer(event)) return;
      this.moved = true;
      this.applyFrom(event);
    }

    onPointerUp(event) {
      if (!this.isActivePointer(event)) return;

      // Applied unthrottled so the divider ends exactly where the pointer left it.
      // A touch that never travelled was a tap, and taps are left alone.
      if (this.moved || event.pointerType !== 'touch') this.applyFrom(event);
      this.endDrag();
    }

    onPointerCancel(event) {
      // The gesture was taken over — a page scroll, most often. Whatever the
      // divider was showing is still what the visitor asked for; leave it there.
      if (!this.isActivePointer(event)) return;
      this.endDrag();
    }

    /** Also rejects the frame-throttled move that can land after the drag ends. */
    isActivePointer(event) {
      return this.pointerId !== null && event.pointerId === this.pointerId;
    }

    endDrag() {
      this.releasePointer();
      delete this.dataset.dragging;
    }

    releasePointer() {
      if (this.pointerId === null) return;
      if (this.hasPointerCapture(this.pointerId)) this.releasePointerCapture(this.pointerId);
      this.pointerId = null;
    }

    /* ---- Value -------------------------------------------------------------- */

    /**
     * Frame-relative pointer position as a whole percentage.
     * @param {PointerEvent} event
     * @returns {number|null} null when the frame has no width yet
     */
    positionFrom(event) {
      const rect = this.getBoundingClientRect();
      if (!rect.width) return null;

      const ratio = (event.clientX - rect.left) / rect.width;
      return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
    }

    applyFrom(event) {
      const value = this.positionFrom(event);
      if (value === null) return;
      this.setValue(value);
    }

    /**
     * Writes through the range input so both input paths share one state.
     * @param {number} value 0–100
     */
    setValue(value) {
      if (Number(this.input.value) === value) return;
      this.input.value = String(value);
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onInput() {
      this.render();
    }

    /** Publishes the input's value to CSS and to assistive technology. */
    render() {
      const value = Number(this.input.value);
      if (Number.isNaN(value)) return;

      this.style.setProperty('--vsr-compare-position', `${value}%`);

      if (!this.valueTextTemplate) return;
      this.input.setAttribute(
        'aria-valuetext',
        this.valueTextTemplate
          .split(BEFORE_TOKEN)
          .join(value)
          .split(AFTER_TOKEN)
          .join(100 - value)
      );
    }
  }

  if (window.VSR) window.VSR.define('vsr-compare', VsrCompare);
  else if (!customElements.get('vsr-compare')) customElements.define('vsr-compare', VsrCompare);
})();
